import * as THREE from 'three'

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value))

const smoothstep = (start, end, value) => {
    const progress = clamp((value - start) / (end - start))
    return progress * progress * (3 - 2 * progress)
}

const seededRandom = (seed) => {
    const value = Math.sin(seed * 78.233 + 11.17) * 43758.5453
    return value - Math.floor(value)
}

const vertexShader = `
    attribute vec3 aScatter;
    attribute float aSize;
    attribute float aPhase;
    attribute float aDetail;
    attribute vec3 aColor;

    uniform float uProgress;
    uniform float uTime;
    uniform float uPixelRatio;

    varying vec3 vColor;
    varying float vAlpha;
    varying float vDetail;

    float easeOutCubic(float value) {
        return 1.0 - pow(1.0 - value, 3.0);
    }

    void main() {
        float delayed = smoothstep(aPhase * 0.16, 1.0, uProgress);
        float gather = easeOutCubic(delayed);
        vec3 transformed = mix(aScatter, position, gather);
        float alive = smoothstep(0.0, 0.22, uProgress);
        float settle = smoothstep(0.7, 1.0, gather);

        transformed.z += sin(uTime * 1.45 + aPhase * 18.0) * 0.025 * settle;
        transformed.xy += vec2(
            cos(uTime * 0.72 + aPhase * 13.0),
            sin(uTime * 0.81 + aPhase * 15.0)
        ) * 0.008 * settle;

        vec4 viewPosition = modelViewMatrix * vec4(transformed, 1.0);
        gl_Position = projectionMatrix * viewPosition;
        float detailSize = mix(1.0, 0.38, aDetail);
        gl_PointSize = aSize * detailSize * uPixelRatio * (7.2 / max(1.0, -viewPosition.z));
        vColor = aColor;
        vAlpha = alive * (0.46 + gather * 0.44) * mix(1.0, 0.22, aDetail);
        vDetail = aDetail;
    }
`

const fragmentShader = `
    varying vec3 vColor;
    varying float vAlpha;
    varying float vDetail;

    void main() {
        vec2 centered = gl_PointCoord - vec2(0.5);
        float distanceToCenter = length(centered);
        if (distanceToCenter > 0.5) discard;

        float edge = mix(0.36, 0.44, vDetail);
        float core = 1.0 - smoothstep(0.05, edge, distanceToCenter);
        float halo = 1.0 - smoothstep(0.18, 0.5, distanceToCenter);
        vec3 softRose = vec3(1.0, 0.72, 0.84);
        vec3 color = mix(vColor, softRose, core * 0.08 * (1.0 - vDetail));
        float alpha = (core * 0.9 + halo * 0.12) * vAlpha;
        gl_FragColor = vec4(color, alpha);
    }
`

export function initGirlfriendParticles() {
    const portrait = document.querySelector('.girlfriend-portrait')
    const image = portrait?.querySelector('img')
    const page = document.getElementById('page-fairy')
    if (!portrait || !image || !page) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        portrait.style.setProperty('--photo-reveal', '1')
        return
    }

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' })
    renderer.setClearColor(0x000000, 0)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.domElement.className = 'girlfriend-portrait__particles'
    renderer.domElement.setAttribute('aria-hidden', 'true')
    portrait.appendChild(renderer.domElement)

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 30)
    camera.position.z = 7
    const particleGroup = new THREE.Group()
    scene.add(particleGroup)

    let points = null
    let material = null
    let progress = 0
    let targetProgress = 0
    let lastTime = performance.now()
    let isVisible = false
    let width = 1
    let height = 1
    const pointer = new THREE.Vector2()
    const pointerTarget = new THREE.Vector2()

    const buildParticles = () => {
        if (!image.naturalWidth || !image.naturalHeight) return

        const rect = portrait.getBoundingClientRect()
        width = Math.max(1, rect.width)
        height = Math.max(1, rect.height)
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5)
        renderer.setPixelRatio(pixelRatio)
        renderer.setSize(width, height, false)
        camera.aspect = width / height
        camera.updateProjectionMatrix()

        const sampleWidth = width < 360 ? 170 : 250
        const sampleHeight = Math.round(sampleWidth * image.naturalHeight / image.naturalWidth)
        const sampleCanvas = document.createElement('canvas')
        const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true })
        sampleCanvas.width = sampleWidth
        sampleCanvas.height = sampleHeight
        sampleContext.drawImage(image, 0, 0, sampleWidth, sampleHeight)

        const pixels = sampleContext.getImageData(0, 0, sampleWidth, sampleHeight).data
        const step = 2
        const worldHeight = 4.45
        const worldWidth = worldHeight * sampleWidth / sampleHeight
        const positions = []
        const scatters = []
        const colors = []
        const sizes = []
        const phases = []
        const details = []
        let index = 0

        for (let y = 0; y < sampleHeight; y += step) {
            for (let x = 0; x < sampleWidth; x += step) {
                const pixelIndex = (y * sampleWidth + x) * 4
                const alpha = pixels[pixelIndex + 3] / 255
                if (alpha < 0.2 || seededRandom(index + 41) > alpha * 0.96) {
                    index += 1
                    continue
                }

                const normalizedX = x / sampleWidth
                const normalizedY = y / sampleHeight
                const red = pixels[pixelIndex] / 255
                const green = pixels[pixelIndex + 1] / 255
                const blue = pixels[pixelIndex + 2] / 255
                const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722
                const faceX = (normalizedX - 0.5) / 0.24
                const faceY = (normalizedY - 0.36) / 0.19
                const faceDepth = Math.exp(-(faceX * faceX + faceY * faceY) * 1.25) * 0.46
                const faceDetail = Math.exp(-(faceX * faceX + faceY * faceY) * 0.82)
                const eyeBand = Math.exp(-(
                    Math.pow((normalizedY - 0.335) / 0.055, 2) +
                    Math.pow((normalizedX - 0.5) / 0.25, 4)
                ))
                const detail = clamp(faceDetail * 0.72 + eyeBand * 0.55)
                const torsoDepth = Math.exp(-Math.pow((normalizedX - 0.5) / 0.42, 2)) * 0.1
                const depth = faceDepth + torsoDepth + (1 - luminance) * 0.08
                const targetX = (normalizedX - 0.5) * worldWidth
                const targetY = (0.5 - normalizedY) * worldHeight
                const angle = seededRandom(index + 3) * Math.PI * 2
                const radius = 2.2 + seededRandom(index + 17) * 4.4

                positions.push(targetX, targetY, depth)
                scatters.push(
                    Math.cos(angle) * radius + (seededRandom(index + 51) - 0.5) * 1.5,
                    Math.sin(angle) * radius + (seededRandom(index + 67) - 0.5) * 1.5,
                    (seededRandom(index + 29) - 0.5) * 8
                )
                colors.push(
                    Math.min(1, red * 1.04 + 0.025),
                    Math.min(1, green * 0.98 + 0.015),
                    Math.min(1, blue * 1.03 + 0.04)
                )
                sizes.push(1.8 + seededRandom(index + 83) * 2.55)
                phases.push(seededRandom(index + 97))
                details.push(detail)
                index += 1
            }
        }

        points?.geometry.dispose()
        material?.dispose()
        if (points) particleGroup.remove(points)

        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
        geometry.setAttribute('aScatter', new THREE.Float32BufferAttribute(scatters, 3))
        geometry.setAttribute('aColor', new THREE.Float32BufferAttribute(colors, 3))
        geometry.setAttribute('aSize', new THREE.Float32BufferAttribute(sizes, 1))
        geometry.setAttribute('aPhase', new THREE.Float32BufferAttribute(phases, 1))
        geometry.setAttribute('aDetail', new THREE.Float32BufferAttribute(details, 1))

        material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uProgress: { value: progress },
                uTime: { value: 0 },
                uPixelRatio: { value: pixelRatio }
            },
            transparent: true,
            depthWrite: false,
            depthTest: true,
            vertexColors: true,
            blending: THREE.NormalBlending
        })

        points = new THREE.Points(geometry, material)
        particleGroup.add(points)
    }

    const updateTarget = () => {
        const rect = page.getBoundingClientRect()
        targetProgress = clamp((window.innerHeight - rect.top) / (window.innerHeight * 0.82))
    }

    const render = (time) => {
        const delta = Math.min((time - lastTime) / 1000, 0.05)
        lastTime = time

        if (isVisible && material) {
            updateTarget()
            progress += (targetProgress - progress) * (1 - Math.exp(-delta * 7.5))
            pointer.lerp(pointerTarget, 1 - Math.exp(-delta * 4.5))

            const gather = smoothstep(0.02, 0.86, progress)
            const reveal = smoothstep(0.58, 0.98, progress)
            const idleYaw = Math.sin(time * 0.00045) * 0.035
            const idlePitch = Math.cos(time * 0.00037) * 0.018

            particleGroup.rotation.y += ((pointer.x * 0.16 + idleYaw) - particleGroup.rotation.y) * (1 - Math.exp(-delta * 4))
            particleGroup.rotation.x += ((-pointer.y * 0.1 + idlePitch) - particleGroup.rotation.x) * (1 - Math.exp(-delta * 4))
            particleGroup.position.z = Math.sin(time * 0.0007) * 0.035 * gather
            material.uniforms.uProgress.value = gather
            material.uniforms.uTime.value = time * 0.001
            portrait.style.setProperty('--photo-reveal', (reveal * 0.92).toFixed(3))
            portrait.style.setProperty('--portrait-depth', gather.toFixed(3))
            renderer.render(scene, camera)
        }

        requestAnimationFrame(render)
    }

    const ready = async () => {
        if (!image.complete) await new Promise(resolve => image.addEventListener('load', resolve, { once: true }))
        if (image.decode) await image.decode().catch(() => {})
        buildParticles()
        updateTarget()
        progress = targetProgress
        portrait.classList.add('is-particle-ready', 'is-3d-particle-ready')
        requestAnimationFrame(render)
    }

    const resizeObserver = new ResizeObserver(buildParticles)
    const visibilityObserver = new IntersectionObserver(([entry]) => {
        isVisible = entry.isIntersecting
    }, { root: document.getElementById('fake-scroll'), rootMargin: '35% 0px' })
    const handlePointerMove = (event) => {
        const rect = page.getBoundingClientRect()
        pointerTarget.x = clamp((event.clientX - rect.left) / rect.width * 2 - 1, -1, 1)
        pointerTarget.y = clamp((event.clientY - rect.top) / rect.height * 2 - 1, -1, 1)
    }
    const handlePointerLeave = () => pointerTarget.set(0, 0)

    resizeObserver.observe(portrait)
    visibilityObserver.observe(page)
    page.addEventListener('pointermove', handlePointerMove, { passive: true })
    page.addEventListener('pointerleave', handlePointerLeave, { passive: true })
    ready()
}
